import React, { Component } from 'react';
import debounce from 'lodash/debounce';
import ReactTimeout from 'react-timeout';
import { TextField, Checkbox, Button, ButtonType, HelpPanel, HelpItemType } from '@riotgames/accounts-react-components';
import { Grid, GridAlignment, GridDirection, GridJustify } from '@riotgames/accounts-react-components/lib/Grid';
import { GridPanel } from '@riotgames/accounts-react-components/lib/Grid/GridPanel';
import cx from 'classnames';
import validator from '../util/validator';
import AppContext from '../AppContext';

const VERIFY_TIME_LIMIT = 600;

class Email extends Component {
  static contextType = AppContext;

  constructor(props) {
    super(props);

    this.state = {
      pending: false,
      validForm: false,
      validationFormMsg: '',
      validationFormRules: {
        confirmPassword: true
      },
      emailVerified: {
        verified: false,
        verifiedTimer: null,
        remainingTime: VERIFY_TIME_LIMIT,
        remainingTimeFormatted: ''
      },
      forms: {
        email: {
          value: '',
          valid: false,
          touched: false,
          infoMsg: '인증 메일 발송 버튼을 눌러 인증을 진행해 주시기 바랍니다.',
          validationMsg: '',
          validationRules: {
            email: true
          }
        },
        confirmCode: {
          value: '',
          valid: false,
          touched: false,
          infoMsg: '인증 번호를 입력해 주세요.',
          validationMsg: '',
          validationRules: {
            fixLength: 6,
            numberic: true,
            isRequired: true
          }
        },
        marketingAgreement: {
          value: false,
          valid: true,
          touched: false,
          validationMsg: ''
        }
      }
    };

    this.updateEmail = this.updateEmail.bind(this);
    this.updateMarketingSubscription = this.updateMarketingSubscription.bind(this);

    this.debouncer = [];
  }

  debounceHandleChange = (name) => {
    if (this.debouncer[name]) {
      return this.debouncer[name];
    }

    this.debouncer[name] = debounce((decouncedInvoke) => {
      decouncedInvoke();
    }, 10);

    return this.debouncer[name];
  };

  changeHandler = async (name, value, rules) => {
    // valid to false when user changes value
    this.setState({
      validForm: false
    });

    await this.updateState(name, value);

    const { forms } = this.state;
    this.debounceHandleChange(name)(async () => {
      const debounceRule = rules || forms[name].lazyValidationRules;

      if (forms[name].valid && debounceRule) {
        await this.updateState(name, value, debounceRule);
      }

      this.debounceHandleChange('form')(() => {
        this.updateValidForm(); // watch out: maybe need to synchronous state.
      });
    });
  };

  updateValidForm = async () => {
    const { forms, validationFormRules } = this.state;
    let validForm = true;
    const validationFormMsg = '';

    for (const identifier in forms) {
      // checking each input field is valid
      validForm = validForm && forms[identifier].valid;
    }

    this.setState({
      validForm,
      validationFormMsg
    });
  };

  updateState = async (name, value, rules) => {
    const updatedFormState = {
      ...this.state.forms
    };
    const updatedElement = {
      ...updatedFormState[name]
    };
    const validationRules = rules ? rules : updatedElement.validationRules;

    const { isValid, validationMsg } = await validator(value, validationRules, {
      actions: this.context.actions
    });
    updatedElement.valid = isValid;
    updatedElement.validationMsg = validationMsg || '';
    updatedElement.touched = true;
    updatedElement.value = value;

    updatedFormState[name] = updatedElement;

    this.setState({
      forms: updatedFormState
    });
  };

  update(params) {
    const { actions } = this.context;
    actions.setData(params);
  }

  moveNext() {
    const {
      emailVerified,
      forms: { email, marketingAgreement }
    } = this.state;

    this.update({
      email: email.value,
      marketingAgreement: {
        email: marketingAgreement.value
      },
      emailVerified: emailVerified.verified
    });

    this.props.handleMoveNext();
  }

  componentWillUnmount() {
    const {
      emailVerified: { verifiedTimer }
    } = this.state;
    this.props.clearTimeout(verifiedTimer);
  }

  resetTimer = (isVerified = false) => {
    const {
      emailVerified: { verifiedTimer }
    } = this.state;

    return new Promise((resolve) =>
      this.setState(
        {
          emailVerified: {
            verified: isVerified,
            verifiedTimer: this.props.clearTimeout(verifiedTimer),
            remainingTime: VERIFY_TIME_LIMIT,
            remainingTimeFormatted: ''
          }
        },
        resolve
      )
    );
  };

  requestVerification = async () => {
    this.setState({ pending: true });
    const {
      actions: { verifyEmail }
    } = this.context;

    const {
      forms: { email }
    } = this.state;

    if (!email.valid) {
      this.setState({ pending: false });
      return false;
    }

    try {
      const { status } = await verifyEmail(email.value);

      if (status !== 201) {
        this.update({
          hasError: true,
          errorDetail: '서버 연결 오류 혹은 유효 시간이 초과되었습니다. 시간이 초과되었다면 처음부터 다시 해주세요.'
        });
        return false;
      }

      const {
        emailVerified,
        emailVerified: { verifiedTimer }
      } = this.state;
      if (verifiedTimer) {
        this.resetTimer();
      }

      this.setState({
        pending: false,
        emailVerified: {
          ...emailVerified,
          verifiedMsg: '6자리 숫자 인증 코드가 발송되었습니다. 서비스에 따라 1분 이상 소요될 수 있습니다.',
          verifiedTimer: this.props.setTimeout(this.tick, 1000)
        }
      });

      this.confirmCodeRef.focus();
    } catch (e) {
      console.warn(e.message);
      this.setState({ pending: false });
      this.update({
        hasError: true,
        errorDetail: '서버 연결 오류 혹은 유효 시간이 초과되었습니다. 시간이 초과되었다면 처음부터 다시 해주세요.'
      });
    }
  };

  tick = () => {
    const {
      emailVerified,
      emailVerified: { verified, remainingTime }
    } = this.state;

    const min = `0${Math.floor(remainingTime / 60)}`;
    const sec = `0${remainingTime % 60}`;
    const remainingTimeFormatted = `${min.substring(min.length - 2)}분 ${sec.substring(sec.length - 2)}초`;

    if (!verified && remainingTime > 0) {
      this.setState({
        emailVerified: {
          ...emailVerified,
          remainingTimeFormatted,
          remainingTime: remainingTime - 1,
          verifiedTimer: this.props.setTimeout(this.tick, 1000)
        }
      });
    } else {
      this.resetTimer();
    }
  };

  confirmVerification = async () => {
    const {
      actions: { confirmVerifyEmail }
    } = this.context;

    const {
      forms: { email, confirmCode }
    } = this.state;

    const payload = {
      target: email.value,
      code: confirmCode.value
    };

    try {
      const { status } = await confirmVerifyEmail(payload);
      const isVerified = true;
      this.resetTimer(isVerified);
      this.moveNext();
    } catch (err) {
      const { status } = err.response;
      let errMsg = '';

      if (status === 403) {
        errMsg = '인증번호가 일치하지 않습니다.';
        this.setConfirmCodeError(errMsg);
      } else {
        errMsg = `서버와의 처리 과정에서 오류가 발생했습니다. 오류코드(${status})`;
        this.update({
          hasError: true,
          shouldReset: true,
          errorHeader: '안내',
          errorDetail: errMsg
        });
      }
    }
  };

  setConfirmCodeError = (errMsg) => {
    const {
      forms,
      forms: { confirmCode }
    } = this.state;

    this.setState({
      forms: {
        ...forms,
        confirmCode: {
          ...confirmCode,
          valid: false,
          validationMsg: errMsg
        }
      }
    });
  };

  updateMarketingSubscription(email) {
    this.setState({ marketingAgreement: { email } });
  }

  updateEmail(email, isVerified) {
    this.setState({ email, emailVerified: isVerified });
  }

  reActiveEmailForm = async () => {
    await this.resetTimer();
    this.emailRef.select();
    this.emailRef.focus();
  };

  buildMsgSendMail() {
    const {
      pending,
      emailVerified: { verifiedMsg, verifiedTimer },
      forms: { email }
    } = this.state;

    if (pending || !!verifiedTimer) {
      return verifiedMsg || email.infoMsg; // set default to info msg for avoiding empty in very short time
    }

    return email.validationMsg || email.infoMsg;
  }

  render() {
    const {
      validForm,
      pending,
      emailVerified: { verifiedTimer, remainingTimeFormatted },
      forms: { email, confirmCode }
    } = this.state;
    const labelEmailSend = !verifiedTimer ? '인증 메일 발송' : '발송 완료';
    const msgSendMail = this.buildMsgSendMail();

    return (
      <GridPanel
        title={'이메일 인증'}
        button={
          <Button
            buttonType={ButtonType.submit}
            label="인증번호 확인"
            disabled={!validForm}
            onClick={this.confirmVerification}
          />
        }
      >
        <Grid direction={GridDirection.vertical} align={GridAlignment.center} onClick={this.reActiveEmailForm}>
          <TextField
            autoFocus={true}
            ref={(input) => (this.emailRef = input)}
            label="이메일주소"
            name="email"
            type="email"
            animate={true}
            value={email.value}
            error={email.touched && !email.valid}
            disabled={!!verifiedTimer}
            onValueChange={this.changeHandler.bind(this, 'email')}
          />
          <HelpPanel
            items={[
              {
                type: email.touched && !email.valid ? HelpItemType.error : HelpItemType.hint,
                text: msgSendMail
              }
            ]}
            animate={false}
          />
          {!verifiedTimer && (
            <Button
              className="mobile-button-fullwidth"
              buttonType={ButtonType.primary}
              label={labelEmailSend}
              disabled={pending || !!verifiedTimer}
              onClick={this.requestVerification}
            />
          )}
        </Grid>

        {!!verifiedTimer && (
          <>
            <Grid direction={GridDirection.vertical} align={GridAlignment.center} style={{ marginTop: '1em' }}>
              <TextField
                ref={(input) => (this.confirmCodeRef = input)}
                label={remainingTimeFormatted}
                name="confirmCode"
                type="number"
                autoComplete="off"
                animate={true}
                animationDelay={0.1}
                value={confirmCode.value}
                disabled={!verifiedTimer}
                error={confirmCode.touched && !confirmCode.valid}
                onValueChange={this.changeHandler.bind(this, 'confirmCode')}
              />
              <HelpPanel
                items={[
                  {
                    type: confirmCode.validationMsg ? HelpItemType.error : HelpItemType.hint,
                    text: confirmCode.validationMsg || confirmCode.infoMsg
                  }
                ]}
                animate={false}
              />
            </Grid>
            <Grid direction={GridDirection.vertical}>
              <Checkbox
                onCheckedChange={this.changeHandler.bind(this, 'marketingAgreement')}
                labelComponent={<>이메일 수신 동의 (라이엇 게임즈의 게임/이벤트 정보의 수신 여부)</>}
              />
            </Grid>
          </>
        )}
      </GridPanel>
    );
  }
}

export default ReactTimeout(Email);
